import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex } from '@chakra-ui/react';

import { Employee, Level, StructureNode } from '@app/api/models/structure';
import { TableColumn, TablePagination } from '@app/components/common/table';
import { useAlert, useApi } from '@app/hooks';
import { Filters } from '@app/api';
import { TableBase } from '@app/components/common/table/table-base';
import { Txt } from '@app/components/common';

import { ChainItem } from '../types';
import { OrgStructureNoData, OrgStructureViewNode } from './node';


type Props = {
  parent?: StructureNode;
  node?: StructureNode;
  levels: Level[];
  onSubmit: (newParent: StructureNode) => void;
  onClose: () => void;
}

export const ResubordinateDialog = ({
  parent,
  node,
  levels,
  onSubmit,
  onClose,
}: Props) => {
  const api = useApi();
  const { success, error } = useAlert();
  const [levelsMap, setLevelsMap] = useState<Map<string, number>>(new Map());
  const [chain, setChain] = useState<ChainItem[]>([]);
  const [data, setData] = useState<StructureNode[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState<TablePagination>({
    page: 1,
    pageSize: 10,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLevelsMap(new Map(levels.map(({ id, value }) => [id.toString(), value])));
    (async () => {
      setLoading(true);

      const { data } = await api.structure.getNodes({
        filter: {
          level_id: levels[0].id.toString(),
          is_active: 'true',
        },
        sort: '+id',
        limit: 10,
        offset: 0,
      });

      setData(data.data);

      setLoading(false);
    })();
  }, [levels]);

  
  const fetchData = useCallback(async ({ 
    boss_id,
    filter,
    pagination
  }: {
    boss_id?: number,
    filter: Filters,
    pagination: TablePagination,
  }) => { 
    setLoading(true);
    const { data } = await api.structure.getNodes({
      filter: { 
        ...filter,
        ...(boss_id ? { boss_id: boss_id?.toString() } : { level_id: levels[0].id.toString() } ), 
        is_active: 'true',
      },

      sort: '+id',
      limit: pagination.pageSize,
      offset: pagination.pageSize * (pagination.page - 1),
    });

    setData(data.data);
    setTotal(data.total);
    setLoading(false);
  }, []);

  const availableLevels = useMemo(() => {
    const nodeLevel = levelsMap.get(node.level.id.toString());
    return levels.filter(({ value }) => value > nodeLevel);
  }, [levels, levelsMap, node.level.id]);

  const tableData = useMemo(() => {
    if (chain.length === 0) {
      return data;
    }
    const sortByLevel = (a: StructureNode, b: StructureNode) => {
      const aLevel = levelsMap.get(a.level.id.toString());
      const bLevel = levelsMap.get(b.level.id.toString());
      if (aLevel === undefined || bLevel === undefined) {
        return 0;
      }
      return bLevel - aLevel;
    };

    const nodeLevel = levelsMap.get(node.level.id.toString());
    const availableLevels = levels.filter(({ value }) => value > nodeLevel);

    return [
      ...chain.map(({ item }) => item),
      ...(data.length > 0 
        ? data.sort(sortByLevel).filter(({ level }) => {
          if (level.id === node.level.id) {return false;}
          return availableLevels.some(({ id }) => id === level.id);
        })
        : [{
          id: -1,
          position: null,
          boss_id: null,
          level: levels[0],
          employee: null,
          route_number: null,
          territory: '-',
          is_active: true,
        }]
      ),
    ]; 
  }, [chain, data, levels, levelsMap]);

  const onItemClick = useCallback(async (item: StructureNode) => {
    if (availableLevels[availableLevels.length - 1].id === item.level.id) {
      return;
    }
    await fetchData({
      pagination: {
        page: 1,
        pageSize: 10,
      },
      filter: {},
      boss_id: item.id,
    });
    setChain([...chain, {
      pagination,
      item,
    }]);
  }, [chain, fetchData, pagination, availableLevels]);

  const onMoveUpClick = useCallback(async (_item: StructureNode) => {
    const index = chain.findIndex(({ item }) => _item.id === item.id);
    if (index === 0) {
      await fetchData({
        pagination: {
          page: 1,
          pageSize: 10,
        },
        filter: {},
        boss_id: undefined,
      });
      setChain([]);
      return;
    }
    
    await fetchData({
      pagination: chain[index].pagination,
      filter: {},
      boss_id: chain[index].item.boss_id,
    });
    setPagination(chain[index].pagination);
    setChain(chain.slice(0, index));
    
  }, [chain, fetchData]);

  const onPaginationChange = useCallback((pagination: TablePagination) => {
    fetchData({
      pagination,
      filter: {},
      boss_id: chain[chain.length - 1]?.item.id,
    });
    setPagination(pagination);
  }, [chain, fetchData]);

  const onResubordinateSubmit = useCallback(async (instance: StructureNode) => {
    try {
      await api.structure.updateNode({
        id: node.id,
        level_id: Number(node.level.id),
        position_id: Number(node.position.id),
        route_number_id: Number(node.route_number?.id),
        territory: node.territory,
        is_active: node.is_active,
        boss_id: instance.id,
      });
      success('Переподчинение успешно выполнено');
    } catch (e) {
      error(e.message);
      return;
    }

    onSubmit(instance);
  }, [onSubmit, api.structure, node.id, node.level.id, node.position.id, node.route_number?.id, node.territory, node.is_active, success, error]);

  const columns = useColumns({ chain, levels, onItemClick, onMoveUpClick, onSelect: onResubordinateSubmit });


  return <Box mx={-6} mb={-10}><TableBase<StructureNode>
    title=""
    columns={columns}
    data={tableData}
    loading={loading}
    total={total}
    pagination={pagination}
    onPaginationChange={onPaginationChange}
  /></Box>;
};

const useColumns = ({ chain, levels, onItemClick, onMoveUpClick, onSelect }: {
  chain: ChainItem[];
  levels: Level[];
  onItemClick: (item: StructureNode) => void;
  onMoveUpClick: (item: StructureNode) => void;
  onSelect: (item: StructureNode) => void;
}): TableColumn<StructureNode>[] => {
  return [
    {
      name: 'territory',
      label: 'Уровень / Территория',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, { rowData, totalRows, rowIndex }) => {
          if (rowData.id === -1) {
            const parentLevelIndex = levels.findIndex(({ id }) => id === chain[chain.length - 1].item.level.id); 
            return <OrgStructureNoData index={parentLevelIndex + 1} />;
          }
          if (!chain.length) {
            return <OrgStructureViewNode
              data={rowData}
              isLastLevel={false}
              index={0}
              parentIndex={0}
              onItemClick={onItemClick}
            />;
          }
          const index = chain.findIndex(({ item }) => item.id === rowData.id);
          const isLastLevel = levels[levels.length - 1].id === rowData.level.id;
          const levelIndex = levels.findIndex(({ id }) => id === rowData.level.id);
          if (index !== -1) {
            const parentLevelIndex = index > 0 ? levels.findIndex(({ id }) => id === chain[index - 1].item.level.id) : -1; 
            return <OrgStructureViewNode
              data={rowData}
              isLastLevel={false}
              index={levelIndex}
              parentIndex={parentLevelIndex}
              onItemClick={onMoveUpClick}
              linkUp={index > 0 ? 1 : undefined}
              linkDown
            />;
          }
          const parentLevelIndex = levels.findIndex(({ id }) => id === chain[chain.length - 1].item.level.id);  

          return <OrgStructureViewNode
            data={rowData}
            index={levelIndex}
            parentIndex={parentLevelIndex}
            isLastLevel={isLastLevel}
            onItemClick={onItemClick}
            linkUp={1}
            linkMore={totalRows > rowIndex + 1} 
          />;
        },
      },
    },
    {
      name: 'employee',
      label: 'Сотрудник / Вакансия',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (employee: Employee | null, { rowData }) => {
          if (rowData.id === -1) {
            return null;
          }
          if (!employee) {
            return <Txt color='red.500'>Вакансия</Txt>;
          }
          return <Box >
            <Txt color='brand.500'>{employee.name}</Txt>
            <Box display='flex'>
              <Txt color='gray.500'>{rowData.position.name}</Txt>
            </Box>
          </Box>;
        },
      },  
    },
    {
      name: 'id',
      label: 'Действия',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (id, { rowData }) => {
          if (rowData.id === -1) {
            return null;
          }
          return (
            <Flex gap={2}>
              <Button onClick={() => onSelect(rowData)} colorScheme='brand' size='sm' >Выбрать</Button>
            </Flex>
          );
        },
      },  
    }
  ];
};